// Cálculo de cotizaciones y ventas: líneas, impuestos y totales. Misma regla que public.billing_line() de la base de datos
// (supabase/migrations/20261003b_billing_base.sql): cada valor se redondea a 2 decimales (mitad hacia arriba).
// Se hace con enteros (BigInt), sin decimales de coma flotante, para que el PDF, la pantalla y la base nunca difieran en un centavo.
// Los casos de src/utils/__tests__/fixtures/billing_cases.json salen de la propia base de datos y esta función debe devolver lo mismo.
// El redondeo del total del documento a peso entero queda por confirmar con el contador y no se aplica aquí.

// Las columnas de dinero son numeric(14,2): hasta 999.999.999.999,99. Dentro de ese rango los centavos caben exactos en un Number.
const MAX_CENTS = 99999999999999n;

const TAX_KINDS = ['iva', 'consumo', 'excluido', 'exento'];
const STOCK_POLICIES = ['block', 'warn', 'allow_negative'];

// "12.5" → 12500n con 3 decimales. Acepta números o textos decimales; rechaza notación científica, negativos y exceso de decimales.
const toScaled = (value, decimals, label) => {
  const raw = typeof value === 'number' ? String(value) : String(value ?? '').trim();
  if (!/^\d+(\.\d+)?$/.test(raw)) throw new RangeError(`${label} inválido`);
  const [int, frac = ''] = raw.split('.');
  if (frac.length > decimals && /[^0]/.test(frac.slice(decimals))) throw new RangeError(`${label}: máximo ${decimals} decimales`);
  return BigInt(int + frac.slice(0, decimals).padEnd(decimals, '0'));
};

// División entera redondeando la mitad hacia arriba (todos los valores son positivos).
const roundDiv = (num, den) => (num * 2n + den) / (2n * den);

const toPesos = (cents) => Number(cents) / 100;

// Una línea: cantidad (3 decimales), precio (2), descuento % (2) e impuesto % (2).
export const calcLineCents = ({ quantity, unit_price, discount_pct = 0, tax_rate = 0 }) => {
  const qty = toScaled(quantity, 3, 'cantidad');
  const price = toScaled(unit_price, 2, 'precio');
  const disc = toScaled(discount_pct, 2, 'descuento');
  const rate = toScaled(tax_rate, 2, 'impuesto');
  if (qty <= 0n) throw new RangeError('cantidad inválida');
  if (disc > 10000n) throw new RangeError('descuento inválido');
  if (rate > 10000n) throw new RangeError('impuesto inválido');
  const gross = roundDiv(qty * price, 1000n);
  const base = roundDiv(qty * price * (10000n - disc), 10000000n);
  const tax = roundDiv(base * rate, 10000n);
  if (gross > MAX_CENTS || base + tax > MAX_CENTS) throw new RangeError('importe fuera de rango');
  return { gross, discount: gross - base, base, tax, total: base + tax };
};

export const calcLine = (line) => {
  const c = calcLineCents(line);
  return { gross: toPesos(c.gross), discount: toPesos(c.discount), base: toPesos(c.base), tax: toPesos(c.tax), total: toPesos(c.total) };
};

// Un documento: suma de líneas. subtotal = bruto; total = bruto − descuento + impuesto (igual que quotes.total en la base).
export const calcDocument = (lines = []) => {
  const sum = { gross: 0n, discount: 0n, tax: 0n };
  const items = lines.map((line) => {
    const c = calcLineCents(line);
    sum.gross += c.gross; sum.discount += c.discount; sum.tax += c.tax;
    return { ...line, line_total: toPesos(c.total) };
  });
  return {
    lines: items,
    subtotal: toPesos(sum.gross),
    discount_total: toPesos(sum.discount),
    tax_total: toPesos(sum.tax),
    total: toPesos(sum.gross - sum.discount + sum.tax)
  };
};

// ¿Una cotización del bot puede aprobarse sola? Misma regla que la base: interruptor activo, tope fijado por el dueño y total dentro del tope.
export const canAutoApprove = (settings, total) => {
  if (!settings || settings.auto_approve !== true) return false;
  const cap = Number(settings.auto_approve_max_amount);
  return Number.isFinite(cap) && cap > 0 && Number(total) <= cap;
};

// Qué hacer cuando no alcanzan las existencias: 'block' no deja, 'warn' avisa y deja, 'allow_negative' deja sin avisar.
export const stockDecision = (policy, stock, needed) => {
  if (!STOCK_POLICIES.includes(policy)) throw new RangeError('política de existencias inválida');
  if (stock === null || stock === undefined) return { allowed: true, warn: false, controlled: false };
  const short = Number(stock) - Number(needed) < 0;
  if (!short) return { allowed: true, warn: false, controlled: true };
  if (policy === 'block') return { allowed: false, warn: false, controlled: true };
  return { allowed: true, warn: policy === 'warn', controlled: true };
};

export const isTaxKind = (k) => TAX_KINDS.includes(k);

// Estados de una cotización y a cuáles puede pasar (igual que la base de datos: la base es quien lo hace cumplir).
export const QUOTE_TRANSITIONS = {
  borrador: ['aprobada', 'reemplazada'],
  aprobada: ['enviada', 'borrador'],
  enviada: ['aceptada', 'rechazada', 'vencida', 'reemplazada'],
  aceptada: [], rechazada: [], vencida: [], reemplazada: []
};
export const canTransition = (from, to) => (QUOTE_TRANSITIONS[from] || []).includes(to);
export const isQuoteEditable = (status) => status === 'borrador';
